import * as Sentry from '@sentry/browser'
import { isAlarmEvent } from './alarm'
import { e2eSentryTags } from './e2eAttribution'
import { moveMessageStackToThreads } from './messageEvents'

const DO_NOT_LOG_DOMAINS = ['stockholmexergi.se']

export function isAbortError(value: unknown): boolean {
  return value instanceof Error && value.name === 'AbortError'
}

export function isNonRetriable404Event(event: Sentry.ErrorEvent, hint?: Sentry.EventHint): boolean {
  const originalException = hint?.originalException as { status?: number } | undefined
  const status = originalException?.status ?? event.tags?.api_error_status

  return status === 404 || status === '404' || event.message?.includes('status: 404') === true
}

// Session expiry — apiClient already logs the user out and redirects, so every in-flight request
// rejecting with a 401 is expected. Matched structurally on `status` (like the 404 filter above)
// rather than by message text, since a 401 with an unparseable body throws a different message
// ("Failed to parse response: ...") but carries the same status.
export function isSessionExpiredEvent(event: Sentry.ErrorEvent, hint?: Sentry.EventHint): boolean {
  const originalException = hint?.originalException as { status?: number } | undefined
  const status = originalException?.status ?? event.tags?.api_error_status

  return status === 401 || status === '401'
}

export function stripSensitiveRequestHeaders(event: Sentry.ErrorEvent): void {
  const headers = event.request?.headers

  if (!headers) {
    return
  }

  delete headers.Authorization
  delete headers.authorization
  delete headers['Impersonating-User']
  delete headers['impersonating-user']
}

export function filterSentryEvent(event: Sentry.ErrorEvent, hint?: Sentry.EventHint): Sentry.ErrorEvent | null {
  if (isAbortError(hint?.originalException)) {
    return null
  }

  if (isNonRetriable404Event(event, hint) && !isAlarmEvent(event)) {
    return null
  }

  if (isSessionExpiredEvent(event, hint)) {
    return null
  }

  event.tags = { ...event.tags, ...e2eSentryTags() }
  stripSensitiveRequestHeaders(event)
  moveMessageStackToThreads(event)

  return event
}

const uuidLikeSegmentPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export function normalizeApiEndpoint(endpoint: string): string {
  return endpoint
    .split('/')
    .map((segment) => {
      if (/^\d+$/.test(segment)) {
        return ':id'
      }

      if (uuidLikeSegmentPattern.test(segment)) {
        return ':uuid'
      }

      return segment
    })
    .join('/')
}

function setScreenResolutionTag() {
  if (typeof window === 'undefined' || !window.screen) {
    return
  }

  const screenWidth = window.screen.width
  const screenHeight = window.screen.height
  const resolution = `${screenWidth}x${screenHeight}`
  let cssCategory = 'Other'

  // Device type classification using CSS breakpoints
  if (screenWidth < 640) {
    cssCategory = 'xs (<640px)'
  } else if (screenWidth < 768) {
    cssCategory = 'sm (640-767px)'
  } else if (screenWidth < 1024) {
    cssCategory = 'md (768-1023px)'
  } else if (screenWidth < 1280) {
    cssCategory = 'lg (1024-1279px)'
  } else if (screenWidth < 1536) {
    cssCategory = 'xl (1280-1535px)'
  } else {
    cssCategory = '2xl (≥1536px)'
  }

  // Set multiple tags for different dashboard views
  Sentry.setTag('screen_resolution.exact', resolution)
  Sentry.setTag('screen_resolution.category', cssCategory)
}

function normalizeError(err: unknown): Error {
  if (err instanceof Error) {
    return err
  }

  if (typeof err === 'string') {
    return new Error(err)
  }

  try {
    return new Error(JSON.stringify(err))
  } catch {
    return new Error('Unknown error')
  }
}

type CaptureMessageContext = {
  level?: Sentry.SeverityLevel
  tags?: Record<string, string | number | boolean>
  extra?: Record<string, unknown>
  fingerprint?: string[]
}

type CaptureExceptionContext = CaptureMessageContext

type BoundaryExceptionContext = {
  boundaryName: string
  extra?: Record<string, unknown>
}

type ApiMessageContext = {
  scope: 'apiClient' | 'spreadsheetApiClient'
  endpoint: string
  status?: number
  url: string
  method?: string
  code?: string
  detail?: string
  body?: unknown
}

type ScopedMessageContext = {
  scope: string
  issueType: string
  message: string
  level?: Sentry.SeverityLevel
  tags?: Record<string, string | number | boolean>
  extra?: Record<string, unknown>
  fingerprint?: string[]
}

const sentryTraceSampleRate = Number.parseFloat(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE ?? '0.1')

export default {
  isInitialized: false,
  init: function (): void {
    if (this.isInitialized) {
      return
    }
    this.isInitialized = true
    Sentry.init({
      dsn: 'https://0dd8348bae2987899e10e243f5da2bba@o4508211764068352.ingest.de.sentry.io/4508212151124048',
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
        Sentry.replayCanvasIntegration(),
        Sentry.captureConsoleIntegration({ levels: ['error'] }),
        Sentry.thirdPartyErrorFilterIntegration({
          filterKeys: ['aurora-react'],
          behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
        }),
      ],
      // The SDK v10 `sendDefaultPii: false` baseline. Leaving `dataCollection` unset in v11 collects
      // cookies, bodies and user info by default.
      dataCollection: {
        userInfo: false,
        cookies: false,
        httpHeaders: {
          request: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
          response: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
        },
        httpBodies: [],
        urlQueryParams: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
        genAI: { inputs: false, outputs: false },
        databaseQueryData: false,
        queues: false,
        graphQL: { document: false, variables: false },
      },
      environment: process.env.REACT_APP_STAGE,
      initialScope: { tags: e2eSentryTags() },
      // Tracing
      tracesSampleRate: sentryTraceSampleRate,
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: [
        'localhost',
        // Match both webapi and api-staging for prod and staging respectively
        /^https:\/\/api-staging?\.aurorabysigholm\.(se|com)\/api/,
        /^https:\/\/webapi?\.aurorabysigholm\.com\/api/,
      ],
      maxValueLength: 10000,
      // Session Replay
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      beforeSend: filterSentryEvent,
      enabled: process.env.NODE_ENV === 'production',

      ignoreErrors: [
        // Browser engine noise — not actionable
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',

        // Non-actionable promise rejections (often from browser extensions)
        'Non-Error promise rejection captured',
        'Non-Error exception captured',

        // Network errors outside our control
        /^NetworkError/,
        'Failed to fetch',
        'Load failed',
        'Network request failed',

        // Stale tabs after deploys — user just needs to refresh
        /ChunkLoadError/,
        /Loading chunk .* failed/,
        /dynamically imported module/,
      ],

      denyUrls: [
        // Browser extensions inject errors into our domain
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
        /^moz-extension:\/\//i,
      ],
    })

    // Set screen resolution tag after Sentry is initialized
    setScreenResolutionTag()
  },
  captureException: function (err: unknown, context?: CaptureExceptionContext): void {
    const normalizedError = normalizeError(err)
    Sentry.captureException(normalizedError, {
      level: context?.level,
      tags: context?.tags,
      fingerprint: context?.fingerprint,
      extra: {
        error_json: JSON.stringify(normalizedError),
        error_message: normalizedError.message,
        ...context?.extra,
      },
    })
  },
  captureBoundaryException: function (err: unknown, context: BoundaryExceptionContext): void {
    this.captureException(err, {
      level: 'error',
      tags: {
        sentry_scope: 'error_boundary',
        boundary_name: context.boundaryName,
      },
      extra: {
        boundaryName: context.boundaryName,
        ...context.extra,
      },
      fingerprint: ['error_boundary', context.boundaryName],
    })
  },
  captureApiMessage: function (context: ApiMessageContext): void {
    const normalizedEndpoint = normalizeApiEndpoint(context.endpoint)
    const status = context.status ?? 0
    const level = status >= 500 ? 'error' : 'warning'

    this.captureMessage(`${context.scope} error. status: ${status}, endpoint: ${normalizedEndpoint}`, {
      level,
      tags: {
        api_client_scope: context.scope,
        api_endpoint: normalizedEndpoint,
        api_error_status: String(context.status ?? 'unknown'),
        ...(context.code ? { api_error_code: context.code } : {}),
      },
      extra: {
        endpoint: context.endpoint,
        url: context.url,
        method: context.method,
        status: context.status,
        code: context.code,
        detail: context.detail,
        body: context.body,
      },
      fingerprint: [context.scope, normalizedEndpoint, String(context.status ?? 'unknown'), context.code ?? 'no_code'],
    })
  },
  captureScopedMessage: function (context: ScopedMessageContext): void {
    this.captureMessage(context.message, {
      level: context.level ?? 'warning',
      tags: {
        sentry_scope: context.scope,
        issue_type: context.issueType,
        ...context.tags,
      },
      extra: context.extra,
      fingerprint: context.fingerprint ?? [context.scope, context.issueType],
    })
  },
  captureMessage: function (message: string, context?: CaptureMessageContext): void {
    Sentry.captureMessage(message, {
      level: context?.level,
      tags: context?.tags,
      extra: context?.extra,
      fingerprint: context?.fingerprint,
    })
  },
  setUser: function (user: User): void {
    // Disable user logging for domains that are in the do not log list
    if (user && DO_NOT_LOG_DOMAINS.includes(user.email.split('@')[1])) {
      return
    }

    Sentry.setUser({ id: user.id })
    Sentry.setTag('user_id', user?.id)
    Sentry.setTag('user_organization_name', user?.organization_name)
  },
  setImpersonatingUser: function (user: User): void {
    Sentry.setTag('impersonating_user_id', user?.id)
  },
  setActiveSystem: function (system: System): void {
    Sentry.setTag('system', system.name)
    Sentry.setTag('organization', system.organization_name)
    Sentry.setTag('system_id', system.id)
    Sentry.setTag('organization_id', system.organization)
  },
  captureRouteVisit: function (route: string): void {
    // Clean the route path (remove query parameters and fragments)
    const cleanRoute = route.split('?')[0].split('#')[0]

    Sentry.setTag('current_route', cleanRoute)
  },
}
